import { inject, observer } from "mobx-react";
import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Switch,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Loader2 } from "lucide-react";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";

interface IProps {
  store?: RootStore;
  product: Product;
  children: React.ReactNode;
}

const DiscontinueProduct: React.FC<IProps> = ({ store, product, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelAll, setCancelAll] = useState(false);
  const { toast } = useToast();

  const discontinueProduct = async () => {
    setLoading(true);

    const { productStore } = store!;

    let success = false;

    if (cancelAll)
      success = await productStore.discontinueProduct(applicationId, product.randmarSKU || "");
    else
      success = await productStore.setProductProperty(applicationId, product.randmarSKU || "", "State", "Discontinued");

    if (success) {
      toast({
        variant: "success",
        title: "Success",
        description: `Product ${product.mpn || ""} discontinued.`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Product ${product.mpn || ""} could not be discontinued...`,
      });
    }

    setOpen(false);
    await productStore.getProduct(applicationId, product.randmarSKU || "");

    setLoading(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !loading && setOpen(isOpen)}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Discontinue {product.mpn}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8 space-y-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p>Please wait...</p>
          </div>
        ) : (
          <>
            <div className="py-4 text-center">
              <p>Also cancel open purchase orders and reseller orders?</p>
              <div className="flex items-center justify-center space-x-2 mt-4">
                <Switch id="cancel-orders" checked={cancelAll} onCheckedChange={setCancelAll} />
                <Label htmlFor="cancel-orders">Cancel open orders</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={discontinueProduct}>Confirm</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default inject("store")(observer(DiscontinueProduct));
